import { useEffect, useRef, useState } from 'react';
import { inputState } from '../../hooks/useInput';
import { useStore } from '../../store/useStore';

export const Joystick = () => {
    const stickRef = useRef<HTMLDivElement>(null);
    const baseRef = useRef<HTMLDivElement>(null);
    const [touchId, setTouchId] = useState<number | null>(null);

    const handleTouchStart = (e: React.TouchEvent) => {
        e.preventDefault();
        if (touchId !== null) return;
        const touch = e.changedTouches[0];
        setTouchId(touch.identifier);
        updateStick(touch.clientX, touch.clientY);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        e.preventDefault();
        if (touchId === null) return;
        const touch = Array.from(e.changedTouches).find(t => t.identifier === touchId);
        if (touch) {
            updateStick(touch.clientX, touch.clientY);
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        e.preventDefault();
        const touch = Array.from(e.changedTouches).find(t => t.identifier === touchId);
        if (touch) {
            setTouchId(null);
            resetStick();
        }
    };

    const updateStick = (clientX: number, clientY: number) => {
        const gameStarted = useStore.getState().gameStarted;
        const isInventoryOpen = useStore.getState().isInventoryOpen;
        const isPaused = useStore.getState().isPaused;
        const isShopOpen = useStore.getState().isShopOpen;
        if (!gameStarted || isInventoryOpen || isPaused || isShopOpen) {
            resetStick();
            return;
        }
        if (!baseRef.current || !stickRef.current) return;
        const baseRect = baseRef.current.getBoundingClientRect();
        const centerX = baseRect.left + baseRect.width / 2;
        const centerY = baseRect.top + baseRect.height / 2;

        const maxDist = baseRect.width / 2;

        const deltaX = clientX - centerX;
        const deltaY = clientY - centerY;
        const dist = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        const clampedDist = Math.min(dist, maxDist);
        const angle = Math.atan2(deltaY, deltaX);

        const stickX = Math.cos(angle) * clampedDist;
        const stickY = Math.sin(angle) * clampedDist;

        stickRef.current.style.transform = `translate(${stickX}px, ${stickY}px)`;

        // Update Input State based on threshold
        const threshold = 0.2;
        const normalizedX = stickX / maxDist;
        const normalizedY = stickY / maxDist;

        inputState.right = normalizedX > threshold;
        inputState.left = normalizedX < -threshold;
        inputState.backward = normalizedY > threshold;
        inputState.forward = normalizedY < -threshold;
    };

    const resetStick = () => {
        if (stickRef.current) {
            stickRef.current.style.transform = `translate(0px, 0px)`;
        }
        inputState.forward = false;
        inputState.backward = false;
        inputState.left = false;
        inputState.right = false;
    };

    return (
        <div style={{
            position: 'absolute',
            bottom: '50px',
            left: '50px',
            width: '120px',
            height: '120px',
            pointerEvents: 'auto',
            zIndex: 2000,
            touchAction: 'none'
        }}>
            {/* Base */}
            <div ref={baseRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                {/* Stick */}
                <div ref={stickRef} style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.5)',
                    position: 'absolute',
                    pointerEvents: 'none'
                }} />
            </div>

            {/* Jump Button (Right Side usually, but putting here for simple test) */}
            <div
                onTouchStart={() => {
                    if (useStore.getState().gameStarted && !useStore.getState().isInventoryOpen && !useStore.getState().isPaused && !useStore.getState().isShopOpen) {
                        inputState.jump = true;
                    }
                }}
                onTouchEnd={() => { inputState.jump = false; }}
                style={{
                    position: 'fixed',
                    bottom: '60px',
                    right: '60px',
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'rgba(255, 0, 0, 0.3)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    userSelect: 'none'
                }}>
                JUMP
            </div>

            {/* Attack Button */}
            <div
                onTouchStart={() => {
                    if (useStore.getState().gameStarted && !useStore.getState().isInventoryOpen && !useStore.getState().isPaused && !useStore.getState().isShopOpen) {
                        inputState.attack = true;
                    }
                }}
                onTouchEnd={() => { inputState.attack = false; }}
                style={{
                    position: 'fixed',
                    bottom: '160px',
                    right: '40px',
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'rgba(0, 0, 255, 0.3)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    userSelect: 'none'
                }}>
                ATK / ACT
            </div>
        </div>
    );
};
